import { IQuery } from 'core-ts-types';
import { APTerm } from '../../../../global/builders/ap_term_builder';
import { APTermsQuery } from '../../../../plan/queries/ap_terms_query';

import * as _4 from 'lodash4';
import { FastConditionalStrategy } from 'utility-ts-strategy';
import { SlotComment } from '../../../../comments/builders/comment_builder';
import { CommentMeta } from '../../../../comments/builders/comment_builder';
import { ICommentsDatasource } from '../../../../comments/datasources/comments_datasource';
import { CommentServant } from '../../../../comments/servants/comment_servant';
import {
  APGenericSlot,
  CourseGroupSlot,
  CourseSlot,
  NCRSlot,
  WildcardSlot
} from '../../../../global/builders/ap_generic_slot_builder';
import { APProgramSlot } from '../../../../global/builders/ap_program_slot_builder';
import { IAPProgramSlotDatasource } from '../../../../global/datasources/ap_program_slot_datasource';
import { APGenericSlotServant } from '../../../../global/servants/ap_generic_slot_servant';
import { IPlanFeedbackGenericSlotBuilder } from '../builders/plan_feedback_generic_slot_builder';
import { IFeedbackTransaction } from '../builders/plan_feedback_transaction_builder';
import { IPlanFeedbackProgramSlotsDatasource } from '../datasources/plan_feedback_program_slots_datasource';
import { PlanFeedbackServant, TemplateChangesTransactionTypeText } from '../servants/plan_feedback_servant';

export type TemplateChangesGroupedByTransactionType = Record<
  IFeedbackTransaction['transactionType'],
  {
    statusGroup: string;
    suggestions: IPlanFeedbackGenericSlotBuilder[];
  }
>;

const ellipsisClass = 'text-ellipsis';

const formTermSuggestionForCourseGroup = (courseGroupSlot: CourseGroupSlot) => ({
  cssClasses: ellipsisClass,
  id: courseGroupSlot.firstCourse.id,
  text: courseGroupSlot.title,
  transactionType: 'NONE',
  transactionNk: '',
  screenReaderText: '',
  slotNk: courseGroupSlot.firstCourse.nk,
  slotTypeId: courseGroupSlot.firstCourse.id,
  slotType: 'COURSE_GROUP'
});

const formTermSuggestionForWildcard = (wildcardSlot: WildcardSlot) => ({
  cssClasses: ellipsisClass,
  id: _4.get(wildcardSlot, 'course.id'),
  text: wildcardSlot.title,
  transactionType: 'NONE',
  transactionNk: '',
  screenReaderText: '',
  slotTypeId: _4.get(wildcardSlot, 'course.id'),
  slotType: 'WILDCARD',
  slotNk: _4.get(wildcardSlot, 'plannedCourseGroup.nk')
});

const formTermSuggestionForNCR = (ncrSlot: NCRSlot) => ({
  cssClasses: ellipsisClass,
  id: ncrSlot.programSlot.id,
  text: ncrSlot,
  transactionType: 'NONE',
  transactionNk: '',
  screenReaderText: '',
  slotTypeId: ncrSlot.programSlot.id,
  slotType: 'NCR',
  slotNk: _4.get(ncrSlot, 'plannedCourseGroup.nk')
});

const formTermSuggestionForCourse = (courseSlot: CourseSlot) => ({
  cssClasses: ellipsisClass,
  id: courseSlot.course.id,
  text: courseSlot.title,
  transactionType: 'NONE',
  transactionNk: courseSlot.course.nk,
  slotNk: courseSlot.course.nk,
  screenReaderText: '',
  slotTypeId: courseSlot.course.id,
  slotType: 'COURSE'
});

const formTemplateSuggestionForProgramSlot = (pgmSlot: APProgramSlot, comment: string) => ({
  cssClasses: ellipsisClass,
  id: pgmSlot.id,
  text: pgmSlot.desc,
  transactionType: 'NONE',
  transactionNk: '',
  screenReaderText: '',
  slotTypeId: pgmSlot.id,
  slotNk: '',
  slotType: 'PROGRAM_SLOT',
  comment
});

const formTermSuggestion = (genericSlot: APGenericSlot) => {
  if (APGenericSlotServant.isCourseGroupSlot(genericSlot)) {
    return formTermSuggestionForCourseGroup(genericSlot);
  }
  if (APGenericSlotServant.isWildcardSlot(genericSlot)) {
    return formTermSuggestionForWildcard(genericSlot);
  }
  if (APGenericSlotServant.isNCRSlot(genericSlot)) {
    return formTermSuggestionForNCR(genericSlot);
  }
  return formTermSuggestionForCourse(genericSlot);
};

export class AdvisorCompareQuery implements IQuery {
  private __result: any = [];

  constructor(
    private ngAPTermsQuery: APTermsQuery,
    private ngCommentsDatasource: ICommentsDatasource,
    private ngAPProgramSlotDatasource: IAPProgramSlotDatasource
  ) {}

  constructTermSuggestions(genericSlots: APGenericSlot[]) {
    const suggestions = _4.map(genericSlots, formTermSuggestion);
    return _4.sortBy(suggestions, 'text');
  }

  constructTerms(terms: APTerm[]) {
    return _4.map(terms, (term: APTerm) => ({
      id: term.id,
      name: term.name,
      creditsRange: term._creditsRange,
      suggestions: this.constructTermSuggestions(term._genericSlots)
    }));
  }

  getTemplateChangesGroupedByTransactionType() {
    const comments = CommentServant.groupCommentsBasedOnType(this.ngCommentsDatasource.metasource().derived());

    const planFeedbackProgramSlots = this.ngAPProgramSlotDatasource
      .values()
      .filter((pgmSlot) => _4.includes(['MET', 'UNMET'], pgmSlot.status));
    return planFeedbackProgramSlots.reduce((groupedSuggestions, suggestion) => {
      if (_4.isEmpty(groupedSuggestions[suggestion.status])) {
        groupedSuggestions[suggestion.status] = {
          statusGroup: TemplateChangesTransactionTypeText[suggestion.status],
          suggestions: []
        };
      }
      const comment = _4.get(comments, `${[suggestion.id]}.message`, '');
      groupedSuggestions[suggestion.status].suggestions.push(formTemplateSuggestionForProgramSlot(suggestion, comment));
      return groupedSuggestions;
    }, {} as TemplateChangesGroupedByTransactionType);
  }

  run(): this {
    this.__result = {
      termsAndGenericSlots: this.constructTerms(this.ngAPTermsQuery.result.plannedTerms),
      templateChanges: this.getTemplateChangesGroupedByTransactionType()
    };

    const templateChanges = _4.values(this.__result.templateChanges);

    const totalSuggestions =
      _4.sumBy(this.__result.termsAndGenericSlots, 'suggestions.length') +
      _4.sumBy(templateChanges, 'suggestions.length');

    this.__result.templateSuggestionsExist = !_4.isEmpty(templateChanges);
    this.__result.totalSuggestions = totalSuggestions;
    this.__result.suggestionsExist = totalSuggestions > 0;
    return this;
  }
  get result() {
    return this.__result;
  }
}
angular.module('capability-ng-academic-planner').service('ngAPAdvisorCompareQuery', AdvisorCompareQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/suggestions/components/plan_feedback/queries/advisor_compare_query.ts